'use client'

import { Monitor, Moon, Sun } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Tooltip } from '@/components/ui/Tooltip'
import { useLocale } from '@/components/i18n/LocaleProvider'
import { applyTheme, nextTheme, readThemePreference, type ThemePreference } from '@/lib/theme'

const ICONS = {
  system: Monitor,
  dark: Moon,
  light: Sun,
} as const

const SIZE_CLASS = {
  header: 'min-h-11 min-w-11',
  inline: 'h-[30px] w-[30px] min-h-0 min-w-0',
} as const

export function ThemeToggle({
  initialPreference = 'system',
  size = 'header',
}: {
  initialPreference?: ThemePreference
  size?: keyof typeof SIZE_CLASS
}) {
  const { t } = useLocale()
  const [preference, setPreference] = useState<ThemePreference>(initialPreference)

  useEffect(() => {
    setPreference(readThemePreference())
  }, [])

  const onToggle = useCallback(() => {
    const next = nextTheme(preference)
    applyTheme(next)
    setPreference(next)
  }, [preference])

  const next = nextTheme(preference)
  const Icon = ICONS[preference]
  const nextHint =
    next === 'dark'
      ? t('common.themeUseDark', 'Use dark theme')
      : next === 'light'
        ? t('common.themeUseLight', 'Use light theme')
        : t('common.themeUseSystem', 'Use system theme')

  return (
    <Tooltip
      content={nextHint}
      className={size === 'inline' ? 'inline-flex items-center justify-center' : undefined}
    >
      <button
        type="button"
        data-docs-theme-toggle
        aria-label={t('common.switchTheme', 'Toggle theme')}
        onClick={onToggle}
        className={`inline-flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground ${SIZE_CLASS[size]}`}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </button>
    </Tooltip>
  )
}
